/** @format — Compact calendar matching documents date filter design */

import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function isSameDay(a?: Date, b?: Date) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function buildMonthGrid(month: Date) {
  const first = startOfMonth(month);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

export default function CompactCalendar({
  month,
  onMonthChange,
  selected,
  onSelect,
  className,
  embedded = false,
}: {
  month: Date;
  onMonthChange: (month: Date) => void;
  selected?: Date;
  onSelect: (date: Date) => void;
  className?: string;
  /** Fluid width for mobile dropdown panel */
  embedded?: boolean;
}) {
  const label = month.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  const days = buildMonthGrid(month);
  const currentMonth = month.getMonth();

  return (
    <div
      className={cn(
        "box-border rounded-lg border border-neutral-200 bg-white shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)]",
        embedded
          ? "w-full max-w-full overflow-hidden p-1"
          : "w-[246px] shrink-0 p-2",
        className,
      )}
    >
      <div className={cn("flex items-center justify-between", embedded ? "mb-1" : "mb-4")}>
        <button
          type="button"
          aria-label="Previous month"
          onClick={() => onMonthChange(addMonths(month, -1))}
          className={cn(
            "flex shrink-0 items-center justify-center rounded-lg border border-neutral-200 bg-white",
            embedded ? "h-6 w-6" : "h-7 w-7",
          )}
        >
          <ChevronLeft className={embedded ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </button>
        <p className={cn("font-medium text-[#0a0a0a]", embedded ? "text-xs" : "text-sm")}>{label}</p>
        <button
          type="button"
          aria-label="Next month"
          onClick={() => onMonthChange(addMonths(month, 1))}
          className={cn(
            "flex shrink-0 items-center justify-center rounded-lg border border-neutral-200 bg-white",
            embedded ? "h-6 w-6" : "h-7 w-7",
          )}
        >
          <ChevronRight className={embedded ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </button>
      </div>

      <div className={cn("grid w-full grid-cols-7", embedded ? "gap-0" : "gap-0.5")}>
        {WEEKDAYS.map((day) => (
          <div key={day} className="flex min-w-0 items-center justify-center py-0.5">
            <span className={cn("text-neutral-500", embedded ? "text-[9px]" : "text-xs")}>{day}</span>
          </div>
        ))}
        {days.map((day) => {
          const outside = day.getMonth() !== currentMonth;
          const isSelected = isSameDay(day, selected);
          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onSelect(day)}
              className={cn(
                "flex min-w-0 items-center justify-center rounded text-[#0a0a0a]",
                embedded ? "aspect-square w-full text-[10px]" : "aspect-square w-full text-sm",
                outside && "opacity-50",
                isSelected ? "bg-blue-50" : "bg-white hover:bg-neutral-50",
              )}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
